/**
 * SeismosEventLog.tsx
 *
 * Replaces both the existing Event Log table AND the separate SeismosInterventionPanel.
 * Renders at stage level only. Each Seismos event row has an inline "Tag as Intervention"
 * action — clicking expands a mini-form row directly beneath (same pattern as StageTable
 * row expansion). Tagged events show a purple intervention badge with edit/delete.
 *
 * Usage — in SeismosScorecard.tsx, replace the event log block + SeismosInterventionPanel with:
 *
 *   {level === 'stage' && events?.length && context.stage_id != null && (
 *     <SeismosEventLog
 *       events={events}
 *       stageId={context.stage_id}
 *       isEditor={isEditor}
 *       apiBase={apiBase}
 *       token={token}
 *     />
 *   )}
 */

import React, { useCallback, useEffect, useState } from 'react';

// ─── Brand (mirror SeismosScorecard) ──────────────────────────────────────────
const BG       = '#181716';
const CARD_BG  = '#242220';
const BORDER   = '#38352f';
const TEXT_PRI = '#EDE8DF';
const TEXT_SEC = '#8a8480';
const TEXT_DIM = '#504d48';
const GOLD     = '#F9C430';
const C_EFF    = '#F9C430';
const C_EHD    = '#7ecb8f';
const C_SEISMOS = '#a78bfa';

const INTERVENTION_TYPES = [
  'Rate Increase',
  'Chemical Ratio Adjustment',
  'Rate Decrease',
  'None',
  'Other',
] as const;

// ─── Types ────────────────────────────────────────────────────────────────────
export interface SeismosEvent {
  id:                   number;
  event_time_ms:        number;
  event_received_time:  number | null;  // epoch ms — for processing time calc
  eff:                  number | null;
  perf_frict:           number | null;
  pipe_frict:           number | null;
  ehd:                  number | null;
  processing_status:    string | null;
  issues:               string | null;
}

interface Intervention {
  id:                number;
  seismos_metrics_id: number | null;
  stage_id:          number;
  intervention_type: string;
  comments:          string | null;
  created_by:        string | null;
  created_at:        string | null;
  event_time:        string | null;
  eff:               number | null;
  ehd:               number | null;
}

interface Props {
  events:    SeismosEvent[];
  stageId:   number;
  isEditor:  boolean;
  apiBase:   string;
  token:     string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtTime = (ms: number) =>
  new Date(ms).toISOString().replace('T', ' ').slice(11, 19);

const s = (v: number | null, dec: number, unit: string) =>
  v != null ? `${v.toFixed(dec)}${unit}` : '—';

const effColor = (v: number | null) =>
  v == null ? TEXT_DIM : v >= 85 ? '#7ecb8f' : v >= 65 ? GOLD : '#e05c5c';

const eventLatencyMs = (ev: SeismosEvent): number | null => {
  if (ev.event_received_time == null || !ev.event_time_ms) return null;
  const diff = ev.event_received_time - ev.event_time_ms;
  return diff > 0 ? diff : null;
};

const latencyColor = (ms: number | null) =>
  ms == null ? TEXT_DIM : ms < 120_000 ? '#7ecb8f' : ms < 300_000 ? GOLD : '#e05c5c';

const fmtLatency = (ms: number | null) => {
  if (ms == null) return '—';
  const s = Math.floor(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

// ─── Inline form state ────────────────────────────────────────────────────────
interface FormState {
  intervention_type: string;
  comments:          string;
}

const BLANK_FORM: FormState = { intervention_type: 'Rate Increase', comments: '' };

// ─── Styles ───────────────────────────────────────────────────────────────────
const thSt: React.CSSProperties = {
  padding: '7px 12px', textAlign: 'right', color: TEXT_SEC, fontSize: 10,
  fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase',
  borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap',
};
const tdSt: React.CSSProperties = { padding: '8px 12px', color: TEXT_PRI };
const inputSt: React.CSSProperties = {
  background: BG, border: `1px solid ${BORDER}`, borderRadius: 4,
  color: TEXT_PRI, padding: '5px 9px', fontSize: 11, fontFamily: 'inherit',
  outline: 'none', width: '100%', boxSizing: 'border-box',
};
const btn = (color: string, disabled = false): React.CSSProperties => ({
  background:   disabled ? 'transparent' : `${color}18`,
  border:       `1px solid ${disabled ? BORDER : color}`,
  borderRadius: 4,
  color:        disabled ? TEXT_DIM : color,
  padding:      '4px 12px',
  fontSize:     10,
  fontWeight:   700,
  cursor:       disabled ? 'not-allowed' : 'pointer',
  letterSpacing: '0.05em',
  fontFamily:   'inherit',
});

// ─── Main component ────────────────────────────────────────────────────────────
export const SeismosEventLog: React.FC<Props> = ({
  events, stageId, isEditor, apiBase, token,
}) => {
  // Map seismos_metrics_id → Intervention (from DB)
  const [taggedMap, setTaggedMap]   = useState<Record<number, Intervention>>({});
  const [loading,   setLoading]     = useState(true);
  const [fetchErr,  setFetchErr]    = useState<string | null>(null);

  // Which event row has its inline form open (null = none)
  const [openEventId, setOpenEventId] = useState<number | null>(null);

  // Inline form state
  const [form,   setForm]   = useState<FormState>(BLANK_FORM);
  const [editId, setEditId] = useState<number | null>(null); // intervention ID being edited
  const [saving, setSaving] = useState(false);
  const [saveErr,setSaveErr] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);

  // ── Fetch existing interventions for this stage ───────────────────────────
  const fetchInterventions = useCallback(async () => {
    setLoading(true); setFetchErr(null);
    try {
      const res = await fetch(
        `${apiBase}/api/seismos-interventions?stage_id=${stageId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const rows: Intervention[] = await res.json();
      // Key by seismos_metrics_id — each event can only be tagged once
      const map: Record<number, Intervention> = {};
      rows.forEach(r => {
        if (r.seismos_metrics_id != null) map[r.seismos_metrics_id] = r;
      });
      setTaggedMap(map);
    } catch (e: any) {
      setFetchErr(e.message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [stageId, apiBase, token]);

  useEffect(() => { fetchInterventions(); }, [fetchInterventions]);

  // ── Open inline form for tagging an event ────────────────────────────────
  const openTag = (ev: SeismosEvent) => {
    const existing = taggedMap[ev.id];
    if (existing) {
      // Edit mode — pre-populate
      setForm({ intervention_type: existing.intervention_type, comments: existing.comments ?? '' });
      setEditId(existing.id);
    } else {
      setForm(BLANK_FORM);
      setEditId(null);
    }
    setSaveErr(null);
    setOpenEventId(openEventId === ev.id ? null : ev.id);  // toggle
  };

  const closeForm = () => { setOpenEventId(null); setEditId(null); setForm(BLANK_FORM); setSaveErr(null); };

  // ── Submit (create or update) ─────────────────────────────────────────────
  const handleSave = async (eventId: number) => {
    setSaving(true); setSaveErr(null);
    try {
      const url    = editId
        ? `${apiBase}/api/seismos-interventions/${editId}`
        : `${apiBase}/api/seismos-interventions`;
      const method = editId ? 'PUT' : 'POST';
      const payload = {
        stage_id:           stageId,
        seismos_metrics_id: eventId,    // links to the specific Seismos event
        intervention_type:  form.intervention_type,
        comments:           form.comments || null,
      };
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      closeForm();
      await fetchInterventions();
    } catch (e: any) {
      setSaveErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (interventionId: number) => {
    if (!window.confirm('Remove this Seismos intervention tag?')) return;
    setDeleting(interventionId);
    try {
      await fetch(`${apiBase}/api/seismos-interventions/${interventionId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      await fetchInterventions();
    } finally { setDeleting(null); }
  };

  const taggedCount = Object.keys(taggedMap).length;

  return (
    <div style={{ marginTop: 20 }}>

      {/* Section header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
        fontSize: 11, color: TEXT_SEC, letterSpacing: '0.08em', textTransform: 'uppercase',
      }}>
        Event Log
        {taggedCount > 0 && (
          <span style={{
            background: `${C_SEISMOS}18`, border: `1px solid ${C_SEISMOS}55`,
            color: C_SEISMOS, borderRadius: 3, padding: '1px 7px',
            fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
          }}>
            {taggedCount} SEISMOS INTERVENTION{taggedCount !== 1 ? 'S' : ''} TAGGED
          </span>
        )}
        {fetchErr && (
          <span style={{ color: '#e05c5c', fontSize: 10 }}>⚠ {fetchErr}</span>
        )}
        {isEditor && taggedCount === 0 && !loading && (
          <span style={{ color: TEXT_DIM, fontSize: 10, marginLeft: 4 }}>
            — click a row to tag a Seismos intervention
          </span>
        )}
      </div>

      <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ position: 'sticky', top: 0, background: CARD_BG, zIndex: 2 }}>
            <tr>
              {['#', 'Time (UTC)', 'Perf Eff %', 'Perf Frict', 'Pipe Frict', 'EHD', 'Proc. Time', 'Status', 'Issues', 'Seismos'].map(h => (
                <th key={h} style={{
                  ...thSt,
                  textAlign: ['#', 'Status', 'Issues', 'Seismos'].includes(h) ? 'center' : 'right',
                  ...(h === 'Seismos' ? { color: C_SEISMOS } : {}),
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {events.map((ev, i) => {
              const tagged   = taggedMap[ev.id];
              const isOpen   = openEventId === ev.id;

              return (
                <React.Fragment key={ev.id}>
                  {/* ── Event data row ────────────────────────────────────── */}
                  <tr
                    style={{
                      borderBottom: isOpen ? 'none' : `1px solid ${BORDER}20`,
                      background:   isOpen ? `${C_SEISMOS}0a` : tagged ? `${C_SEISMOS}06` : 'transparent',
                      cursor:       isEditor ? 'pointer' : 'default',
                      transition:   'background 0.12s',
                    }}
                    onClick={() => isEditor && openTag(ev)}
                    onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = `${GOLD}08`; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isOpen ? `${C_SEISMOS}0a` : tagged ? `${C_SEISMOS}06` : 'transparent'; }}
                  >
                    <td style={{ ...tdSt, textAlign: 'center', color: TEXT_DIM }}>{i + 1}</td>
                    <td style={{ ...tdSt, textAlign: 'right', color: TEXT_SEC, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtTime(ev.event_time_ms)}
                    </td>
                    <td style={{ ...tdSt, textAlign: 'right', color: effColor(ev.eff), fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {s(ev.eff, 1, '%')}
                    </td>
                    <td style={{ ...tdSt, textAlign: 'right', color: TEXT_SEC, fontVariantNumeric: 'tabular-nums' }}>
                      {s(ev.perf_frict, 0, ' psi')}
                    </td>
                    <td style={{ ...tdSt, textAlign: 'right', color: TEXT_SEC, fontVariantNumeric: 'tabular-nums' }}>
                      {s(ev.pipe_frict, 0, ' psi')}
                    </td>
                    <td style={{ ...tdSt, textAlign: 'right', color: C_EHD, fontVariantNumeric: 'tabular-nums' }}>
                      {s(ev.ehd, 3, ' in')}
                    </td>
                    <td style={{ ...tdSt, textAlign: 'right', color: latencyColor(eventLatencyMs(ev)), fontVariantNumeric: 'tabular-nums' }}>
                      {fmtLatency(eventLatencyMs(ev))}
                    </td>
                    <td style={{ ...tdSt, textAlign: 'center' }}>
                      {ev.processing_status ? (
                        <span style={{
                          color:      ev.processing_status === 'successful' ? '#7ecb8f' : '#e0b84a',
                          fontWeight: 600, fontSize: 9,
                          background: ev.processing_status === 'successful' ? '#7ecb8f18' : '#e0b84a18',
                          border:     `1px solid ${ev.processing_status === 'successful' ? '#7ecb8f44' : '#e0b84a44'}`,
                          borderRadius: 3, padding: '1px 5px',
                        }}>{ev.processing_status}</span>
                      ) : <span style={{ color: TEXT_DIM }}>—</span>}
                    </td>
                    <td style={{ ...tdSt, textAlign: 'center', color: TEXT_DIM, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={ev.issues ?? undefined}>
                      {ev.issues
                        ? <span style={{ color: '#e0b84a', fontSize: 10 }}>⚠ {ev.issues.length > 30 ? ev.issues.slice(0, 28) + '…' : ev.issues}</span>
                        : '—'}
                    </td>

                    {/* ── Seismos tag cell ─────────────────────────────── */}
                    <td style={{ ...tdSt, textAlign: 'center', whiteSpace: 'nowrap' }}
                        onClick={e => { e.stopPropagation(); isEditor && openTag(ev); }}>
                      {tagged ? (
                        <span style={{
                          background:   `${C_SEISMOS}18`,
                          border:       `1px solid ${C_SEISMOS}55`,
                          color:        C_SEISMOS,
                          borderRadius: 3, padding: '2px 7px',
                          fontSize:     9, fontWeight: 700,
                          cursor:       isEditor ? 'pointer' : 'default',
                        }}>
                          {tagged.intervention_type}
                        </span>
                      ) : isEditor ? (
                        <span style={{
                          color:        TEXT_DIM,
                          fontSize:     10,
                          border:       `1px dashed ${BORDER}`,
                          borderRadius: 3, padding: '2px 6px',
                          cursor:       'pointer',
                        }}>
                          + Tag
                        </span>
                      ) : (
                        <span style={{ color: TEXT_DIM }}>—</span>
                      )}
                    </td>
                  </tr>

                  {/* ── Inline form row (expands below the event row) ─────── */}
                  {isOpen && (
                    <tr style={{ background: `${C_SEISMOS}0a`, borderBottom: `1px solid ${BORDER}20` }}>
                      <td colSpan={10} style={{ padding: '10px 16px' }}>
                        <div style={{
                          border: `1px solid ${C_SEISMOS}33`, borderRadius: 6,
                          padding: '12px 14px', background: '#1a1816',
                        }}>
                          <div style={{ fontSize: 10, color: C_SEISMOS, fontWeight: 700,
                                        letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>
                            {editId ? 'Edit Intervention Tag' : 'Tag as Seismos Intervention'}
                            <span style={{ color: TEXT_DIM, fontWeight: 400, fontSize: 9, marginLeft: 8 }}>
                              Event #{ev.id} · {fmtTime(ev.event_time_ms)} UTC
                              {ev.eff  != null ? ` · Eff ${ev.eff.toFixed(1)}%` : ''}
                              {ev.ehd  != null ? ` · EHD ${ev.ehd.toFixed(3)}"` : ''}
                            </span>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr auto auto', gap: '0 12px', alignItems: 'end' }}>
                            {/* Intervention type */}
                            <div>
                              <div style={{ fontSize: 9, color: TEXT_SEC, letterSpacing: '0.05em',
                                            textTransform: 'uppercase', marginBottom: 4 }}>
                                Intervention Type
                              </div>
                              <select
                                value={form.intervention_type}
                                onChange={e => setForm(f => ({ ...f, intervention_type: e.target.value }))}
                                style={inputSt}
                              >
                                {INTERVENTION_TYPES.map(t => (
                                  <option key={t} value={t}>{t}</option>
                                ))}
                              </select>
                            </div>

                            {/* Comments */}
                            <div>
                              <div style={{ fontSize: 9, color: TEXT_SEC, letterSpacing: '0.05em',
                                            textTransform: 'uppercase', marginBottom: 4 }}>
                                Comments <span style={{ color: TEXT_DIM }}>(optional)</span>
                              </div>
                              <input
                                type="text"
                                placeholder='e.g. "Opened rate to address low EHD on event 3"'
                                value={form.comments}
                                onChange={e => setForm(f => ({ ...f, comments: e.target.value }))}
                                onKeyDown={e => e.key === 'Enter' && handleSave(ev.id)}
                                style={inputSt}
                              />
                            </div>

                            {/* Save */}
                            <button
                              onClick={() => handleSave(ev.id)}
                              disabled={saving}
                              style={btn(C_SEISMOS, saving)}
                            >
                              {saving ? 'Saving…' : editId ? 'Update' : 'Save'}
                            </button>

                            {/* Cancel / Delete */}
                            <div style={{ display: 'flex', gap: 6 }}>
                              {editId && (
                                <button
                                  onClick={() => handleDelete(editId)}
                                  disabled={deleting === editId}
                                  style={btn('#e05c5c', deleting === editId)}
                                >
                                  {deleting === editId ? '…' : 'Remove'}
                                </button>
                              )}
                              <button onClick={closeForm} style={btn(TEXT_SEC)}>
                                Cancel
                              </button>
                            </div>
                          </div>

                          {saveErr && (
                            <div style={{ color: '#e05c5c', fontSize: 11, marginTop: 8 }}>⚠ {saveErr}</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Tagged interventions summary (if any) */}
      {taggedCount > 0 && (
        <div style={{
          marginTop: 10, padding: '6px 10px',
          background: `${C_SEISMOS}08`, border: `1px solid ${C_SEISMOS}22`,
          borderRadius: 5, fontSize: 10, color: TEXT_SEC,
          display: 'flex', flexWrap: 'wrap', gap: '4px 16px',
        }}>
          {Object.values(taggedMap).map(iv => (
            <span key={iv.id}>
              <span style={{ color: C_SEISMOS, fontWeight: 700 }}>#{iv.seismos_metrics_id}</span>
              {' '}→{' '}
              <span style={{ color: TEXT_PRI }}>{iv.intervention_type}</span>
              {iv.comments && <span style={{ color: TEXT_DIM }}> · {iv.comments}</span>}
              {iv.created_by && <span style={{ color: TEXT_DIM }}> · by {iv.created_by}</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default SeismosEventLog;